/**
 * Надходження від Фонду — список (канон Норми #29, 2026-08-23): InfiniteDocGrid
 * (серверна пагінація/сортування), фільтри рядком під назвою, колонки
 * «Вкладення» і «Підпис».
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { fundReceipts, type FundReceiptListItem } from '@/api/fund_receipts';
import { orgs } from '@/api/catalogs';
import { useOrgFilter } from '@/store/defaults';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import PeriodFilter, { useHrPeriodFilter, MONTHS_UK } from '@/shared/components/PeriodFilter';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function FundReceiptsPage() {
  const navigate = useNavigate();
  const [orgId, setOrgId] = useOrgFilter();
  const per = useHrPeriodFilter();

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m = new Map<string, string>();
    orgList.data?.items.forEach((o) => m.set(o.id, o.name));
    return m;
  }, [orgList.data]);

  const { datasource, total } = useInfiniteGridDatasource<FundReceiptListItem>(
    (limit, offset, sort) => fundReceipts.list({
      organization_id: orgId || undefined,
      ...per.params,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId, ...per.key],
    { attachmentsType: 'doc.fund_receipts' },
  );
  const sc = useSignControlInfinite<FundReceiptListItem>('doc.fund_receipts', orgId);

  const cols = useMemo<ColDef<FundReceiptListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: FundReceiptListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№', width: 140 },
    {
      field: 'organization_id', headerName: 'Організація', width: 200,
      valueFormatter: (p) => p.value ? (orgName.get(p.value) ?? '—') : '',
    },
    {
      field: 'document_date', headerName: 'Дата', width: 120,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
    {
      colId: 'period', headerName: 'Період', width: 140,
      valueGetter: (p) => p.data ? `${MONTHS_UK[p.data.period_month]} ${p.data.period_year}` : '',
    },
    {
      field: 'fund_marker', headerName: 'Фонд', width: 100,
      valueFormatter: (p) => p.data ? (p.value === 'WORK_ACCIDENT_FUND' ? 'Фонд НВ' : 'ПФУ') : '',
    },
    {
      field: 'total_amount', headerName: 'Сума', width: 130,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 110,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [orgName]);

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>Надходження від Фонду<TitleTotal total={total} /></h2>
        <button className="primary" onClick={() => navigate('/fund-receipts/new')}>
          + Нове надходження
        </button>
      </div>
      <div className="filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0 0 0.6rem', alignItems: 'center' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ maxWidth: 260 }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
        <PeriodFilter {...per.bind} />
      </div>
      <InfiniteDocGrid<FundReceiptListItem>
        copyRow={(r) => navigate(`/fund-receipts/new?copy=${r.id}`)}
        deleteRow={{ remove: (id) => fundReceipts.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="fund-receipts"
        onRowDoubleClicked={(r) => navigate(`/fund-receipts/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
